/**
 * ScaledPaper — responsive scaling wrapper around PagedDocument.
 *
 * The paper is a real 8.5in × 11in letter sheet (816px × 1056px at
 * 96dpi). We scale via CSS `zoom` (participates in layout, unlike
 * transform: scale).
 *
 * Two modes:
 *   - Auto-fit (default) — zoom recomputes on pane resize so the
 *     paper always fills the available width within [MIN, MAX].
 *   - Manual override — toolbar − / + or preset menu sets a fixed
 *     zoom. Override survives splitter drags but resets on page
 *     reload (auto-fit is the friendlier default).
 *
 * Form-pane interplay:
 *   - On every manual zoom (step or numeric preset), we fire
 *     onPaperWidthChange with the paper's required width in px.
 *     The splitter (EditorSplitPane.accommodatePaperWidth) decides
 *     what to do: shrink the form when the paper would overflow,
 *     grow it back toward the user's preferred percent when there's
 *     room. This makes "zoom in then zoom out" land the form back
 *     where the user originally had it.
 *   - "Fit to page" (resetToAuto) doesn't fire — autoZoom always
 *     fits within the current pane by definition, so there's
 *     nothing to accommodate.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { Minus, Plus, ChevronDown } from 'lucide-react';

const PAPER_NATURAL_PX = 816;
const PANE_SIDE_PADDING = 32;
const MIN_SCALE = 0.5;
const MAX_SCALE = 1.6;
const STEP = 0.1;

/** Word-style preset menu items. "Fit" resets to auto-fit. */
const PRESETS: Array<{ label: string; value: number | 'auto' }> = [
  { label: '200%', value: 2.0 },
  { label: '150%', value: 1.5 },
  { label: '125%', value: 1.25 },
  { label: '100%', value: 1.0 },
  { label: '75%', value: 0.75 },
  { label: '50%', value: 0.5 },
  { label: 'Fit to page', value: 'auto' },
];

interface Props {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  'data-tour'?: string;
  toolbarExtra?: ReactNode;
  topLeftBadge?: ReactNode;
  /** Fired on every manual zoom change (step button or numeric
   *  preset) with the paper's required width in pixels. The
   *  splitter uses this to shrink the form when the paper would
   *  otherwise overflow, or grow it back toward the user's
   *  preferred percent when zoom-out frees up room. Not fired
   *  for the "Fit to page" preset (auto-fit always fits). */
  onPaperWidthChange?: (paperRequiredPx: number) => void;
}

export const ScaledPaper = forwardRef<HTMLDivElement, Props>(function ScaledPaper(
  {
    children,
    className,
    style,
    toolbarExtra,
    topLeftBadge,
    onPaperWidthChange,
    ...rest
  },
  forwardedRef,
) {
  const innerRef = useRef<HTMLDivElement>(null);
  useImperativeHandle(forwardedRef, () => innerRef.current as HTMLDivElement, []);

  const [autoZoom, setAutoZoom] = useState(1);
  const [zoomOverride, setZoomOverride] = useState<number | null>(null);
  const effectiveZoom = zoomOverride ?? autoZoom;
  const [presetOpen, setPresetOpen] = useState(false);

  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const recompute = () => {
      const available = el.clientWidth - PANE_SIDE_PADDING * 2;
      const next = Math.max(
        MIN_SCALE,
        Math.min(MAX_SCALE, available / PAPER_NATURAL_PX),
      );
      setAutoZoom(next);
    };
    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /** Set zoom (manual override) and report the paper's new required
   *  width so the splitter can shrink/grow the form pane. We always
   *  report — direction-aware logic now lives in the splitter
   *  (compare requested width vs. preferred percent vs. container
   *  width). Auto-fit changes never fire (recompute updates autoZoom
   *  without going through here). */
  const applyZoom = useCallback(
    (next: number, source: 'step' | 'preset') => {
      const clamped = Math.max(MIN_SCALE, Math.min(MAX_SCALE, +next.toFixed(2)));
      setZoomOverride(clamped);
      const paperRequiredPx = clamped * PAPER_NATURAL_PX + PANE_SIDE_PADDING * 2;
      onPaperWidthChange?.(paperRequiredPx);
      void source;
    },
    [onPaperWidthChange],
  );

  const stepZoom = useCallback(
    (delta: number) => {
      applyZoom((zoomOverride ?? autoZoom) + delta, 'step');
    },
    [applyZoom, autoZoom, zoomOverride],
  );

  const resetToAuto = useCallback(() => {
    setZoomOverride(null);
    setPresetOpen(false);
    // Auto-fit shrinks the paper to fit the pane — no form nudge
    // is appropriate here.
  }, []);

  const pickPreset = useCallback(
    (value: number | 'auto') => {
      setPresetOpen(false);
      if (value === 'auto') {
        resetToAuto();
        return;
      }
      applyZoom(value, 'preset');
    },
    [applyZoom, resetToAuto],
  );

  // Click-away to close the preset menu.
  const presetWrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!presetOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (
        presetWrapperRef.current &&
        !presetWrapperRef.current.contains(e.target as Node)
      ) {
        setPresetOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [presetOpen]);

  const isOverridden = zoomOverride !== null;
  const displayPct = Math.round(effectiveZoom * 100);

  return (
    <div
      ref={innerRef}
      className={'relative overflow-auto px-8 pb-10 pt-4 ' + (className ?? '')}
      style={style}
      {...rest}
    >
      {/* Top toolbar row — sits ABOVE the paper, sticky on scroll.
          Mockup .preview-toolbar: cream/85 background + backdrop-blur
          so the bar reads as a transparent overlay on the paper "desk"
          and the document stays the visual focus. */}
      <div className="sticky top-0 z-10 mb-3 flex items-center justify-between gap-2 bg-cream/85 px-2 py-1 backdrop-blur">
        <div>{topLeftBadge}</div>
        <div className="flex items-center gap-2">
          {toolbarExtra && <div>{toolbarExtra}</div>}
          {/* Zoom pill — pine fill with cream content. Mockup
              .zoom-pill: rounded-full + p-1 + soft shadow + cream
              hover-tinted buttons. */}
          <div className="inline-flex items-center gap-0.5 rounded-full bg-pine p-1 shadow-[0_1px_2px_rgba(43,42,47,0.10)]">
            <button
              type="button"
              onClick={() => stepZoom(-STEP)}
              disabled={effectiveZoom <= MIN_SCALE + 0.001}
              title="Zoom out"
              aria-label="Zoom out"
              className="flex h-[22px] w-[22px] items-center justify-center rounded-full text-cream transition-colors hover:bg-cream/15 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <Minus size={13} strokeWidth={2.5} />
            </button>

            {/* Percent + preset dropdown — cream 11px 700 tabular */}
            <div ref={presetWrapperRef} className="relative">
              <button
                type="button"
                onClick={() => setPresetOpen((v) => !v)}
                title="Zoom presets"
                aria-haspopup="listbox"
                aria-expanded={presetOpen}
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold tabular-nums text-cream transition-colors hover:bg-cream/15"
              >
                <span>{displayPct}%</span>
                <ChevronDown size={10} strokeWidth={2.5} />
              </button>

              {presetOpen && (
                // Preset menu — bone surface with charcoal/12 border
                // (matches the dropdown / select chrome family used
                // elsewhere). Active row gets pine-soft tint.
                <div
                  role="listbox"
                  className="absolute right-0 top-full z-20 mt-1.5 w-44 overflow-hidden rounded-xl border border-charcoal/12 bg-bone shadow-[0_12px_32px_rgba(43,42,47,0.12)]"
                >
                  {PRESETS.map((p, idx) => {
                    const isActive =
                      p.value === 'auto'
                        ? !isOverridden
                        : isOverridden &&
                          Math.abs(effectiveZoom - p.value) < 0.005;
                    const isFitToPage = p.value === 'auto';
                    return (
                      <button
                        key={p.label}
                        type="button"
                        role="option"
                        aria-selected={isActive}
                        onClick={() => pickPreset(p.value)}
                        className={
                          'flex w-full items-center justify-between px-3.5 py-1.5 text-[12px] transition-colors ' +
                          (isFitToPage && idx > 0
                            ? 'border-t border-charcoal/8 '
                            : '') +
                          (isActive
                            ? 'bg-pine/[0.08] font-medium text-pine'
                            : 'text-charcoal hover:bg-pine/[0.06] hover:text-pine')
                        }
                      >
                        <span>{p.label}</span>
                        {isActive && (
                          <span className="text-[10px] text-pine">●</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => stepZoom(STEP)}
              disabled={effectiveZoom >= MAX_SCALE - 0.001}
              title="Zoom in"
              aria-label="Zoom in"
              className="flex h-[22px] w-[22px] items-center justify-center rounded-full text-cream transition-colors hover:bg-cream/15 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <Plus size={13} strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </div>

      {/* `zoom` scales BOTH visual size and layout box. */}
      <div style={{ zoom: effectiveZoom }}>{children}</div>
    </div>
  );
});
